<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{
      zIndex: '1100',
    }"
    @hide="handleHideGiftModal"
    :pt="{
      root: {
        class: 'gift-modal-root'
      },
    }"
  >
    <template #container="{ closeCallback }">
      <div
        class="gift-modal-container"
        :style="{
          background:
            (currentStep?.type === 'welcome' && !isDesktop)
              ? `linear-gradient(180deg,rgba(171, 183, 246, 0.5) 0%,rgba(208, 203, 255, 0) 100%)`
              : `#FFFFFF`,
          backgroundSize: 'auto 50%',
          backgroundPosition:'top center',
          backgroundRepeat:'no-repeat',
        }"
      >
        <!-- Header -->
        <div class="gift-header">
          <div class="gift-header-top">
            <div class="gift-header-left">
              <custom-button
                @click="closeCallback"
                class="save-exit-button"
                severity="info"
              >
                <i class="pi pi-chevron-left"></i>
                {{ currentStep?.type === 'welcome' ? "Назад" : "Сохранить и выйти" }}
              </custom-button>
            </div>

            <div class="gift-header-center">
              <div v-if="currentStep?.type === 'welcome'" class="gift-title title">
                {{ giftStore.giftModal.config?.title || 'Подбор подарка' }}
              </div>
            </div>

            <div class="gift-header-right"></div>
          </div>
          <div class="gift-header-bottom">
            <div v-if="currentStep && currentStep.type !== 'welcome' && currentStep.type !== 'completion'" class="progress-container">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${progressPercentage}%` }"
                ></div>
              </div>
              <span class="progress-text helper-text">{{ giftModal.currentStepIndex + 1 }} из {{ totalSteps }}</span>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="gift-content">
          <DynamicStepRenderer v-if="!giftModal.isLoadingConfig" />
          <div v-else class="loading-container">
            <div class="loading-spinner"></div>
            <p>Загрузка конфигурации...</p>
          </div>
        </div>

        <!-- Footer -->
        <div 
          v-if="showFooter" 
          class="gift-footer"
          :class="{ 'has-saved-indicator': showSavedIndicator }"
        >
          <!-- Saved indicator for steps with data -->
          <div v-if="showSavedIndicator" class="answers-saved-indicator">
            <span class="answers-saved-text">Ответы сохранены</span>
            <i class="pi pi-bookmark answers-saved-icon"></i>
          </div>
          
          <!-- Buttons container -->
          <div class="footer-buttons">
            <custom-button
              v-if="showBackButton"
              @click="prevStep"
              severity="danger"
              rounded
              class="back-btn"
              label="Назад"
            />
            <custom-button
              v-if="showNextButton"
              @click="handleNext"
              class="next-btn"
              :label="nextButtonLabel"
              :loading="giftModal.isLoading"
              rounded
            />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Dialog from "primevue/dialog";

import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";

import { useGiftModal } from "@/features/gift/store";
import DynamicStepRenderer from "./GiftSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const giftStore = useGiftModal();
const giftModal = computed(() => giftStore.giftModal);
const currentStep = computed(() => giftStore.currentStep);
const totalSteps = computed(() => giftStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());
const previousStepId = ref<string | null>(null);

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((giftModal.value.currentStepIndex + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isFirstStep = giftModal.value.currentStepIndex === 0;
  
  // Hide back button on special steps
  if (stepType === 'completion') {
    return false;
  }
  
  return !isFirstStep;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isLastStep = giftModal.value.currentStepIndex >= totalSteps.value - 1;
  
  // Hide next button on special steps
  if (stepType === 'completion') {
    return false;
  }
  
  return !isLastStep;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === 'welcome') return "Начать тест";
  
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide footer on special steps
  return stepType !== 'completion';
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;
  
  // Не показываем плашку для исключенных шагов
  if (stepType === "welcome" || stepType === "completion") {
    return false;
  }
  
  // Показываем если есть данные для текущего шага и шаг в списке сохраненных
  const hasData = giftStore.giftModal.giftData[stepId] !== undefined && 
                  giftStore.giftModal.giftData[stepId] !== null &&
                  (Array.isArray(giftStore.giftModal.giftData[stepId]) 
                    ? giftStore.giftModal.giftData[stepId].length > 0
                    : true);
  
  return hasData && savedSteps.value.has(stepId);
});

function handleHideGiftModal() {
  giftStore.hideGiftModal();
}

function prevStep() {
  giftStore.prevStep();
}

function handleNext() {
  if (!currentStep.value) return;
  
  giftStore.nextStep();
}

watch(
  () => giftModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      // Сбрасываем состояние при закрытии модального окна
      savedSteps.value.clear();
      previousStepId.value = null;
    }
  }
);

// Отслеживаем изменения в giftData и добавляем ID шага в список сохраненных
watch(
  () => giftStore.giftModal.giftData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;
    
    // Не отслеживаем исключенные шаги
    if (stepType === "welcome" || stepType === "completion") {
      return;
    }
    
    // Проверяем, есть ли данные для текущего шага
    const hasData = newData[stepId] !== undefined && 
                    newData[stepId] !== null &&
                    (Array.isArray(newData[stepId]) 
                      ? newData[stepId].length > 0
                      : true);
    
    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// Отслеживаем смену шага и проверяем, есть ли уже данные для нового шага
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    
    const stepType = currentStep.value.type;
    
    // Не обрабатываем исключенные шаги
    if (stepType === "welcome" || stepType === "completion") {
      previousStepId.value = newStepId;
      return;
    }
    
    // Проверяем, есть ли уже данные для этого шага
    const stepData = giftStore.giftModal.giftData[newStepId];
    const hasData = stepData !== undefined && 
                    stepData !== null &&
                    (Array.isArray(stepData) ? stepData.length > 0 : true);
    
    if (hasData) {
      savedSteps.value.add(newStepId);
    }
    
    previousStepId.value = newStepId;
  }
);
</script>

<style>
.gift-modal-root {
  width: 60vw;
  max-width: 1020px;
  height: auto;
  max-height: 90vh;
  overflow: auto;
}

@media (max-width: 1024px) {
  .gift-modal-root {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
  }
}
</style>

<style scoped>
.gift-modal-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.gift-title {
  display: none;
}

.gift-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding: 20px 20px 0 20px;
}

.gift-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.gift-header-bottom {
  width: 100%;
}

.gift-header-left {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
}

.gift-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}

.gift-header-center {
  flex: 1;
  text-align: center;
}

.save-exit-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  color: var(--active-100);
  cursor: pointer;
  transition: background-color 0.2s;
}

.progress-container {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4px;
  align-items: flex-start;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: var(--light-blue-20);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--active-100);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--active-100);
}

.gift-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  align-self: stretch;
}

.gift-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  padding: 20px 40px 40px 40px;
}

.gift-footer.has-saved-indicator {
  justify-content: space-between;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.answers-saved-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 160px;
  flex-shrink: 0;
}

.answers-saved-text {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.21;
  letter-spacing: -0.04em;
  color: #6E85CB;
  white-space: nowrap;
}

.answers-saved-icon {
  font-size: 16px;
  color: var(--active-100);
  flex-shrink: 0;
}

@media (max-width: 1024px) {
  .gift-modal-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }

  .back-btn {
    flex: 1;
  }

  .next-btn {
    flex: 1;
  }

  .gift-header {
    padding: 20px 20px 0 20px;
  }

  .gift-title {
    display: block;
    white-space: nowrap;
  }

  .gift-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }

  .gift-header-bottom {
    margin-top: 30px;
  }

  .gift-footer {
    flex-direction: column;
    gap: 20px;
    padding: 0 20px 20px 20px;
  }

  .gift-footer.has-saved-indicator {
    justify-content: space-between;
  }

  .answers-saved-indicator {
    width: 100%;
    justify-content: center;
    order: 1;
  }

  .footer-buttons {
    width: 100%;
    order: 2;
  }

  .gift-header-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
    white-space: nowrap;
  }

  .gift-header-right {
    display: flex;
    height: 45px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 10px;
    flex: 1 0 0;
  }
}

/* Desktop styles - плашка слева, кнопки справа */
@media (min-width: 1024px) {
  .gift-footer {
    flex-direction: row;
    align-items: center;
  }

  .gift-footer.has-saved-indicator {
    justify-content: space-between;
  }

  .answers-saved-indicator {
    width: 160px;
  }

  .footer-buttons {
    flex-shrink: 0;
  }
}
</style>

